import { UpdateListingRequest, CreateListingRequest } from '@/types/listing';

/**
 * Selectors for listing data transformation and normalization
 */

export interface ListingFormData {
  // Basic fields
  id: string;
  title: string;
  description: string;
  listingType: 'auction' | 'direct-purchase' | null;
  
  // Date/time fields
  startTime?: Date;
  endTime?: Date;
  
  // Auction fields
  startingPrice: string;
  reservePrice: string;
  minimumBidIncrement: string;
  bidTimeBuffer: string;
  enableSpecialPricing: boolean;
  specialStartingPrice: string;
  /** When true, if auction ends with reserve not reached we auto-extend (re-activate). */
  autoRenewIfReserveNotReached?: boolean;
  /** Number of days to extend when auto-renewing (1-20). Default 7. */
  autoRenewDays?: number | null;
  
  // Buy Now fields (for auctions with direct purchase option)
  enableDirectPurchase?: boolean;
  directPurchaseType?: 'percentage' | 'fixed';
  directPurchasePercentage?: string; // e.g., "10" for 10%
  directPurchaseFixedPrice?: string;
  
  // Direct purchase fields
  directPrice: string;
  /** When set and > directPrice, listing shows as sänkt pris */
  originalDirectPrice?: string;
  enableReducedDirectPrice?: boolean;
  selectedVehiclePrice: string;
  
  // Delivery fees
  deliveryFeeZoneA: string;
  deliveryFeeZoneB: string;
  deliveryFeeZoneC: string;
  directPurchaseDeliveryFeeZoneA: string;
  directPurchaseDeliveryFeeZoneB: string;
  directPurchaseDeliveryFeeZoneC: string;
  /** When true, hide AutoUncle rating intervals on the public listing page. */
  hideRatingIntervals?: boolean;
  /** When true, show bid history on the public listing page. */
  showBidHistory?: boolean;
}

export interface CreateListingFormData extends Omit<ListingFormData, 'id'> {
  // Additional fields needed for create
  sellerId: string;
  campaignId: string;
  carTestId: string;
  startTime: Date;
  endTime: Date;
  hideRatingIntervals?: boolean;
}

/**
 * Converts form data to UpdateListingRequest
 * Handles all the mapping and normalization
 */
export const createUpdateListingRequest = (formData: ListingFormData): UpdateListingRequest => {
  const baseRequest: UpdateListingRequest = {
    id: formData.id,
    title: formData.title,
    description: formData.description,
    deliveryFee: {
      zoneA: parseFloat(formData.deliveryFeeZoneA),
      zoneB: parseFloat(formData.deliveryFeeZoneB),
      zoneC: parseFloat(formData.deliveryFeeZoneC),
      zoneD: 0,
    },
    // Include startTime and endTime if provided
    ...(formData.startTime && { startTime: formData.startTime instanceof Date ? formData.startTime : new Date(formData.startTime) }),
    ...(formData.endTime && { endTime: formData.endTime instanceof Date ? formData.endTime : new Date(formData.endTime) }),
    ...(formData.hideRatingIntervals !== undefined
      ? { hideRatingIntervals: formData.hideRatingIntervals }
      : {}),
    ...(formData.showBidHistory !== undefined
      ? { showBidHistory: formData.showBidHistory === true }
      : {}),
  };

  if (formData.listingType === 'auction') {
    let buyNowMode: 'off' | 'fixed' | 'percentage' | undefined;
    let buyNowFixed: number | null | undefined;
    let buyNowPercentage: number | null | undefined;

    if (formData.enableDirectPurchase === false) {
      buyNowMode = 'off';
      buyNowFixed = null;
      buyNowPercentage = null;
    } else if (formData.enableDirectPurchase === true) {
      if (formData.directPurchaseType === 'fixed') {
        buyNowMode = 'fixed';
        buyNowPercentage = null;
        if (formData.directPurchaseFixedPrice) {
          buyNowFixed = parseFloat(formData.directPurchaseFixedPrice);
        }
      } else if (formData.directPurchaseType === 'percentage') {
        buyNowMode = 'percentage';
        buyNowFixed = null;
        if (formData.directPurchasePercentage) {
          buyNowPercentage = parseFloat(formData.directPurchasePercentage);
        }
      }
    }

    return {
      ...baseRequest,
      bidTimeBuffer: parseInt(formData.bidTimeBuffer, 10),
      ...(buyNowMode !== undefined ? { buyNowMode } : {}),
      ...(buyNowFixed !== undefined ? { buyNowFixed } : {}),
      ...(buyNowPercentage !== undefined ? { buyNowPercentage } : {}),
      auctionDetails: {
        minimumBidIncrement: parseFloat(formData.minimumBidIncrement),
        startingPrice: parseFloat(formData.startingPrice),
        reservationPrice: parseFloat(formData.reservePrice),
        specialPricing: formData.enableSpecialPricing && formData.specialStartingPrice
          ? parseFloat(formData.specialStartingPrice)
          : undefined,
        autoRenewIfReserveNotReached: formData.autoRenewIfReserveNotReached ?? false,
        autoRenewDays: formData.autoRenewIfReserveNotReached && formData.autoRenewDays != null ? formData.autoRenewDays : null,
      },
    };
  } else {
    return {
      ...baseRequest,
      deliveryFee: {
        zoneA: parseFloat(formData.directPurchaseDeliveryFeeZoneA),
        zoneB: parseFloat(formData.directPurchaseDeliveryFeeZoneB),
        zoneC: parseFloat(formData.directPurchaseDeliveryFeeZoneC),
        zoneD: 0,
      },
      directPurchaseDetails: {
        currentPrice: parseFloat(formData.directPrice),
        ...(formData.enableReducedDirectPrice &&
        formData.originalDirectPrice &&
        parseFloat(formData.originalDirectPrice) > parseFloat(formData.directPrice)
          ? { originalPrice: parseFloat(formData.originalDirectPrice) }
          : {}),
      },
    };
  }
};

/**
 * Converts form data to CreateListingRequest
 * Handles all the mapping and normalization for creating new listings
 */
export const createCreateListingRequest = (formData: CreateListingFormData): CreateListingRequest => {
  // DEBUG: Uncomment for debugging listing creation
  // console.log('[listingSelectors] createCreateListingRequest called with formData:', {
  //   listingType: formData.listingType,
  //   enableDirectPurchase: formData.enableDirectPurchase,
  //   directPurchaseType: formData.directPurchaseType,
  //   directPurchasePercentage: formData.directPurchasePercentage,
  //   directPurchaseFixedPrice: formData.directPurchaseFixedPrice,
  // });

  const baseRequest: CreateListingRequest = {
    listingType: formData.listingType!,
    title: formData.title,
    description: formData.description,
    startTime: formData.startTime,
    endTime: formData.endTime,
    sellerId: formData.sellerId,
    deliveryFee: {
      zoneA: parseFloat(formData.deliveryFeeZoneA),
      zoneB: parseFloat(formData.deliveryFeeZoneB),
      zoneC: parseFloat(formData.deliveryFeeZoneC),
      zoneD: 0,
    },
    campaignId: formData.campaignId,
    carTestId: formData.carTestId,
    hideRatingIntervals: formData.hideRatingIntervals === true,
    showBidHistory: formData.showBidHistory === true,
  };

  if (formData.listingType === 'auction') {
    // Determine buyNowFixed or buyNowPercentage based on directPurchaseType
    let buyNowFixed: number | undefined;
    let buyNowPercentage: number | undefined;
    
    if (formData.enableDirectPurchase) {
      // DEBUG: Uncomment for debugging direct purchase pricing
      // console.log('[listingSelectors] enableDirectPurchase is true, type:', formData.directPurchaseType);
      if (formData.directPurchaseType === 'fixed' && formData.directPurchaseFixedPrice) {
        buyNowFixed = parseFloat(formData.directPurchaseFixedPrice);
        // DEBUG: Uncomment for debugging
        // console.log('[listingSelectors] Setting buyNowFixed (fixed):', buyNowFixed);
      } else if (formData.directPurchaseType === 'percentage' && formData.directPurchasePercentage) {
        // Send as percentage value (e.g., 10 for 10%), backend will divide by 100
        buyNowPercentage = parseFloat(formData.directPurchasePercentage);
        // DEBUG: Uncomment for debugging
        // console.log('[listingSelectors] Setting buyNowPercentage:', buyNowPercentage);
      }
    }

    return {
      ...baseRequest,
      auctionDetails: {
        minimumBidIncrement: parseFloat(formData.minimumBidIncrement),
        startingPrice: parseFloat(formData.startingPrice),
        bidTimeBuffer: parseInt(formData.bidTimeBuffer, 10),
        reservationPrice: parseFloat(formData.reservePrice),
        buyNowFixed,
        buyNowPercentage,
        specialPricing: formData.enableSpecialPricing && formData.specialStartingPrice
          ? parseFloat(formData.specialStartingPrice)
          : undefined,
        autoRenewIfReserveNotReached: formData.autoRenewIfReserveNotReached ?? false,
        autoRenewDays: formData.autoRenewIfReserveNotReached && formData.autoRenewDays != null ? formData.autoRenewDays : null,
      },
    };
  } else {
    return {
      ...baseRequest,
      deliveryFee: {
        zoneA: parseFloat(formData.directPurchaseDeliveryFeeZoneA),
        zoneB: parseFloat(formData.directPurchaseDeliveryFeeZoneB),
        zoneC: parseFloat(formData.directPurchaseDeliveryFeeZoneC),
        zoneD: 0,
      },
      directPurchaseDetails: {
        currentPrice: parseFloat(formData.directPrice),
        originalPrice:
          formData.enableReducedDirectPrice &&
          formData.originalDirectPrice &&
          parseFloat(formData.originalDirectPrice) >
            parseFloat(formData.directPrice)
            ? parseFloat(formData.originalDirectPrice)
            : parseFloat(formData.directPrice),
      },
    };
  }
};
